import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import ChecklistRow from './checklist-row';
import { isStepDone, summarizeRemaining } from 'tio-employee/utils/tas/evidence-checklist';
import Button from 'tio-ui/components/button';
import ErrorMessages from 'tio-ui/components/error-messages';
import { PhUploadSimple } from 'ember-phosphor-icons';
import AcceptConditions from 'tio-employee/components/tuition-assistance/forms/accept-conditions';
import type { SubmitEvidenceBundle } from './submit-evidence';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';

interface SubmitEvidenceActionsSignature {
  Args: {
    application: TasApplicationModel;
    /** Optional so isolated tests can render without the ManagePage-owned
     * bundle; every real render passes one. */
    evidence?: SubmitEvidenceBundle;
  };
  Element: HTMLDivElement;
}

const noop = () => {};

/**
 * What the evidence card *asks the employee to commit to*: the program's
 * evidence terms, and the Submit that acts on them.
 *
 * Split out of SubmitEvidenceBody so it can render in the task card's footer —
 * outside the collapsible region. The terms used to sit inside the body, so an
 * employee who never expanded the card never saw the agreement they were being
 * asked to accept. The legacy show page renders them flat and always visible;
 * this restores that.
 *
 * The grade rows, GPA field and uploads stay in the body: those are the detail
 * you open the card to work through, not the commitment it closes on.
 *
 * `@evidence.canSubmit` is computed by ManagePage and mirrors the legacy show
 * page's full canSubmitApplication gate, including the eligibility lock
 * (`inEligible`/`dynamicEligibility`, fetched by manage-route.ts the same way
 * the legacy show route fetches it) — so a disabled button here can be the
 * employer's policy rather than an unfinished checklist.
 */
export default class SubmitEvidenceActions extends Component<SubmitEvidenceActionsSignature> {
  @service declare intl: IntlService;

  get programTemplate() {
    return this.args.application.tasProgramInstance?.tasProgramTemplate;
  }

  get isTermsDone(): boolean {
    return this.args.evidence ? isStepDone(this.args.evidence.checklist, 'terms') : false;
  }

  /** Outstanding requirements as prose, empty once the checklist is satisfied. */
  get remainingSummary(): string {
    return summarizeRemaining(
      this.args.evidence?.checklist.remaining ?? [],
      this.intl.primaryLocale
    );
  }

  /**
   * Whether submitting is a thing this application can currently do at all.
   *
   * The card deliberately renders in review and settled states, where Submit is
   * disabled because no such transition exists rather than because anything is
   * outstanding. Explaining a block there would tell someone their employer's
   * rules are stopping a request they already submitted.
   */
  get isSubmittable(): boolean {
    return this.args.application.state === 'TAS.ApplicationState.ATTEND';
  }

  /**
   * Why Submit is disabled, or an empty string when it is not.
   *
   * Two distinct causes, and conflating them is what made the old silent
   * disabled button unfixable from the employee's side: unfinished checklist
   * items they can act on, versus an employer eligibility lock they cannot.
   */
  get blockedReason(): string {
    if (!this.args.evidence || this.args.evidence.canSubmit || !this.isSubmittable) {
      return '';
    }
    if (this.remainingSummary) {
      return this.intl.t('tuition_assistance.applications.manage.tasks.checklist_submit_blocked', {
        items: this.remainingSummary,
      });
    }
    return this.intl.t(
      'tuition_assistance.applications.manage.tasks.checklist_submit_blocked_policy'
    );
  }

  /**
   * Province `application_approve_evidence_workflow` — the evidence-time terms
   * specifically, distinct from the course-approval and program-level sets the
   * same template carries for other moments in the lifecycle.
   */
  get conditionsForSubmit(): readonly ConditionModel[] {
    return this.programTemplate?.applicationApproveEvidenceConditions ?? [];
  }

  <template>
    <div class="tio-col gap-4" data-test-submit-evidence-actions ...attributes>
      {{#if this.conditionsForSubmit.length}}
        <ChecklistRow
          @title={{t "tuition_assistance.applications.manage.tasks.checklist_terms_title"}}
          @isDone={{this.isTermsDone}}
        >
          <AcceptConditions
            @conditions={{this.conditionsForSubmit}}
            @onChange={{if @evidence.onAcceptConditions @evidence.onAcceptConditions noop}}
          />
        </ChecklistRow>
      {{/if}}

      <div class="tio-row flex-wrap items-center gap-3">
        <Button
          @intent="primary"
          data-test-submit-evidence-cta
          @isDisabled={{not @evidence.canSubmit}}
          @isRunning={{@evidence.isSubmitting}}
          {{on "click" (if @evidence.onSubmit @evidence.onSubmit noop)}}
        >
          <PhUploadSimple @size={{16}} class="mr-2" />
          {{t "tuition_assistance.applications.manage.tasks.submit_evidence_cta"}}
        </Button>
        {{#if this.blockedReason}}
          <span class="tio-text-sm-normal tio-fg-secondary" data-test-submit-evidence-blocked>
            {{this.blockedReason}}
          </span>
        {{/if}}
        {{#if @evidence.submitError}}
          <ErrorMessages
            @showErrors={{true}}
            @error={{@evidence.submitError}}
            class="text-sm mt-2"
          />
        {{/if}}
      </div>
    </div>
  </template>
}
